import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import AnimatedSection from '../components/common/AnimatedSection';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Building, Factory, Landmark, MapPin,
  Mountain, Trees, Ship, Wheat, Leaf, Droplet, Store, Sun, Train
} from 'lucide-react';
import { regions } from '../data/mock';

const iconMap = {
  building: Building,
  landmark: Landmark,
  factory: Factory,
  mountain: Mountain,
  trees: Trees,
  ship: Ship,
  wheat: Wheat,
  leaf: Leaf,
  droplet: Droplet,
  store: Store,
  sun: Sun,
  train: Train
};

const TerritoriesPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const content = t('territoriesPage');
  const [selectedRegion, setSelectedRegion] = useState(null);

  const sectionIcons = [Factory, Building, Landmark];

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className={`section ${isDark ? 'bg-gradient-to-b from-[#1C1917] to-[#0C0A09]' : 'bg-gradient-to-b from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Souveraineté Économique</span>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-8">
                {content.title}
              </h1>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading-2 mb-4">Carte des pôles économiques</h2>
              <p className="body-large">Chaque territoire devient un centre de production et d'investissement.</p>
            </AnimatedSection>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Map */}
              <AnimatedSection>
                <div className={`relative aspect-[4/5] rounded-2xl p-8 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
                  <svg viewBox="0 0 100 120" className="w-full h-full">
                    {/* Country outline */}
                    <path
                      d="M25 15 L45 10 L65 12 L80 20 L85 35 L82 55 L78 75 L65 90 L50 100 L35 95 L20 80 L15 60 L18 40 L25 15 Z"
                      fill={isDark ? '#5B2D8E' : '#5B2D8E'}
                      fillOpacity={isDark ? '0.15' : '0.05'}
                      stroke="#5B2D8E"
                      strokeWidth="0.5"
                    />
                    
                    {/* Region markers */}
                    {regions.map((region) => (
                      <g 
                        key={region.id}
                        className="cursor-pointer"
                        onClick={() => setSelectedRegion(region)}
                      >
                        <circle
                          cx={region.x}
                          cy={region.y}
                          r={selectedRegion?.id === region.id ? 4 : 2.5}
                          fill={selectedRegion?.id === region.id ? region.color : region.color}
                          fillOpacity={selectedRegion?.id === region.id ? 1 : 0.7}
                          className="transition-all duration-300"
                        />
                        <circle
                          cx={region.x}
                          cy={region.y}
                          r={selectedRegion?.id === region.id ? 7 : 5}
                          fill="none"
                          stroke={region.color}
                          strokeWidth="0.3"
                          opacity={selectedRegion?.id === region.id ? 0.8 : 0.3}
                          className="transition-all duration-300"
                        />
                        {selectedRegion?.id === region.id && (
                          <text
                            x={region.x}
                            y={region.y - 10}
                            textAnchor="middle"
                            className={`text-[3px] font-semibold ${isDark ? 'fill-white' : 'fill-[#5B2D8E]'}`}
                          >
                            {region.name}
                          </text>
                        )}
                      </g>
                    ))}

                    {/* Connection lines */}
                    <path
                      d="M42 78 L38 58 L42 45 L38 22"
                      stroke="#5B2D8E"
                      strokeWidth="0.2"
                      fill="none"
                      opacity="0.2"
                      strokeDasharray="2,2"
                    />
                    <path
                      d="M22 42 L38 58 L28 85"
                      stroke="#5B2D8E"
                      strokeWidth="0.2"
                      fill="none"
                      opacity="0.2"
                      strokeDasharray="2,2"
                    />
                    <path
                      d="M38 58 L62 38"
                      stroke="#5B2D8E"
                      strokeWidth="0.2"
                      fill="none"
                      opacity="0.2"
                      strokeDasharray="2,2"
                    />
                  </svg>

                  {/* Legend */}
                  <div className={`absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#5B2D8E] rounded-full" />
                        <span>Violet</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#F7C948] rounded-full" />
                        <span>Or</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#0D9488] rounded-full" />
                        <span>Turquoise</span>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Region Info */}
              <AnimatedSection delay={0.2}>
                <div className="space-y-6">
                  {selectedRegion ? (
                    <div className={`p-8 rounded-2xl border ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-stone-200'}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {(() => {
                          const IconComponent = iconMap[selectedRegion.icon] || MapPin;
                          return (
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${selectedRegion.color}20` }}
                            >
                              <IconComponent 
                                className="w-6 h-6" 
                                style={{ color: selectedRegion.color }}
                              />
                            </div>
                          );
                        })()}
                        <h3 className="heading-3">{selectedRegion.name}</h3>
                      </div>
                      <span 
                        className="inline-block px-3 py-1 rounded-full text-sm mb-4 text-white"
                        style={{ backgroundColor: selectedRegion.color }}
                      >
                        {selectedRegion.type === 'district' ? 'District autonome' : 'Pôle régional'}
                      </span>
                      <p className="body-medium">
                        Centre de production et d'investissement stratégique pour le développement territorial.
                      </p>
                    </div>
                  ) : (
                    <div className={`p-8 rounded-2xl border ${isDark ? 'bg-[#1C1917]/50 border-white/10' : 'bg-[#F5F0E8]/50 border-stone-200'}`}>
                      <p className="body-medium text-center">
                        Cliquez sur un point de la carte pour explorer les territoires.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {regions.slice(0, 6).map((region) => {
                      const IconComponent = iconMap[region.icon] || MapPin;
                      return (
                        <button
                          key={region.id}
                          onClick={() => setSelectedRegion(region)}
                          className={`p-4 text-left rounded-lg border transition-all flex items-center gap-3 ${
                            selectedRegion?.id === region.id
                              ? 'border-[#5B2D8E] bg-[#5B2D8E]/5'
                              : isDark
                                ? 'border-white/10 hover:border-[#5B2D8E]/50 bg-[#1C1917]'
                                : 'border-[#E8E0D4] hover:border-[#5B2D8E]/50'
                          }`}
                        >
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${region.color}20` }}
                          >
                            <IconComponent 
                              className="w-5 h-5" 
                              style={{ color: region.color }}
                            />
                          </div>
                          <div>
                            <span className={`text-sm font-semibold block ${isDark ? 'text-stone-200' : 'text-[#2D2D2D]'}`}>
                              {region.name}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                              {region.type === 'district' ? 'District' : 'Ville'}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      {content.sections.map((section, index) => {
        const Icon = sectionIcons[index];
        return (
          <section 
            key={index} 
            className={`section ${index % 2 === 0 
              ? isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]' 
              : isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'
            }`}
          >
            <div className="container mx-auto px-4 lg:px-10">
              <div className="max-w-4xl mx-auto">
                <AnimatedSection>
                  <div className="flex items-start gap-6 mb-8">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/5'}`}>
                      <Icon className={`w-8 h-8 ${isDark ? 'text-[#7C4DAF]' : 'text-[#5B2D8E]'}`} />
                    </div>
                    <div>
                      <h2 className="heading-2 mb-2">{section.title}</h2>
                      <div className="divider" />
                    </div>
                  </div>
                </AnimatedSection>

                <div className="space-y-6 pl-0 lg:pl-[88px]">
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <AnimatedSection key={pIndex} delay={pIndex * 0.1}>
                      <p className="text-lg text-theme-secondary leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
                        {paragraph}
                      </p>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="section bg-[#5B2D8E]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-1 text-white mb-6">
                Bâtir ensemble
              </h2>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <p className="text-xl text-white/80 mb-10" style={{ fontFamily: 'var(--font-body)' }}>
                Chaque citoyen est acteur du développement de son territoire.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <Link 
                to="/rejoindre" 
                className="btn-secondary"
              >
                Rejoindre
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TerritoriesPage;
